import React from 'react';
import {
  SimpleGrid,
  Paper,
  Group,
  Text,
  ThemeIcon,
  Stack,
  Badge,
  Skeleton
} from '@mantine/core';
import {
  IconMessageCircle,
  IconCircleCheck,
  IconClock,
  IconShieldCheck
} from '@tabler/icons-react';
import { BusinessStatsProps } from './BusinessStats.types';

/**
 * BusinessStats Component
 *
 * Displays business statistics in a grid of stat cards
 *
 * Features:
 * - Total mentions count
 * - Verified mentions count
 * - Pending mentions count
 * - Business verification status
 *
 * Usage:
 * ```tsx
 * <BusinessStats
 *   totalMentions={25}
 *   verifiedMentions={20}
 *   pendingMentions={5}
 *   isActive={true}
 * />
 * ```
 */
export const BusinessStats: React.FC<BusinessStatsProps> = ({
  totalMentions,
  verifiedMentions,
  pendingMentions,
  isActive,
  loading = false
}): React.ReactElement => {
  // Stats data
  const stats = [
    {
      title: 'Total Mentions',
      value: totalMentions,
      icon: IconMessageCircle,
      color: 'blue'
    },
    {
      title: 'Verified Mentions',
      value: verifiedMentions,
      icon: IconCircleCheck,
      color: 'green'
    },
    {
      title: 'Pending Review',
      value: pendingMentions,
      icon: IconClock,
      color: 'orange'
    },
    {
      title: 'Business Status',
      value: isActive ? 'Active' : 'Inactive',
      icon: IconShieldCheck,
      color: isActive ? 'green' : 'red',
      isBadge: true
    }
  ];

  if (loading) {
    return (
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {[1, 2, 3, 4].map((i) => (
          <Paper key={i} withBorder p="md" radius="md">
            <Stack gap="xs">
              <Skeleton height={24} width="60%" />
              <Skeleton height={32} width="40%" />
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>
    );
  }

  return (
    <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
      {stats.map((stat) => (
        <Paper key={stat.title} withBorder p="md" radius="md">
          <Group justify="apart">
            <Stack gap={4}>
              <Text size="xs" c="dimmed" fw={500} tt="uppercase">
                {stat.title}
              </Text>
              {stat.isBadge ? (
                <Badge
                  size="lg"
                  variant="dot"
                  color={stat.color}
                  leftSection={
                    <ThemeIcon
                      size="sm"
                      variant="transparent"
                      color={stat.color}
                    >
                      <stat.icon size={14} />
                    </ThemeIcon>
                  }
                >
                  {stat.value}
                </Badge>
              ) : (
                <Text fw={700} size="xl">
                  {stat.value}
                </Text>
              )}
            </Stack>

            <ThemeIcon
              size="xl"
              radius="md"
              variant="light"
              color={stat.color}
            >
              <stat.icon size={22} />
            </ThemeIcon>
          </Group>

        </Paper>
      ))}
    </SimpleGrid>
  );
};
